import React, { createContext, useEffect, useMemo, useState } from "react";

import { ISpotlightData, ISpotlightItem } from  "@/interfaces/spotlight";
import { getSpotlightData } from  "@/api/spotlight";
import { transformToSpotlightData } from  "@/helpers/Spotlight";



export interface ISpotlightContext {
    isLoading: boolean,
    setIsLoading: (isLoading: boolean) => void,
    isLoadingSpotlightItem: boolean,
    setIsLoadingSpotlightItem: (isLoading: boolean) => void,
    getSpotlight: any,
    spotlightData: ISpotlightData | undefined,
    setSpotlightData: (spotlightData: ISpotlightData) => void,
    spotlightItem: ISpotlightItem | undefined,
    setSpotlightItem: (spotlightItem: ISpotlightItem) => void,
}

const SpotlightDefaults: ISpotlightContext = {
    isLoading: false,
    setIsLoading: () => {},
    isLoadingSpotlightItem: false,
    setIsLoadingSpotlightItem: () => {},
    spotlightData: undefined,
    getSpotlight: () => {},
    setSpotlightData: () => {},
    spotlightItem: undefined,
    setSpotlightItem: () => {},
};

export const SpotlightContext = createContext(SpotlightDefaults);

const SpotlightProvider = ({children}:{children:React.ReactNode}) => {
    const [spotlightData, setSpotlightData] = useState<ISpotlightData>();
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingSpotlightItem, setIsLoadingSpotlightItem] = useState(false);
    const [spotlightItem, setSpotlightItem] = useState<ISpotlightItem>();
    let fetchContentController: AbortController | undefined;

    useEffect(() => {
        if (spotlightData?.items.length) {
            getSpotlightItem();
        }
    }, [spotlightData]);
    
    function getSpotlight() {
        if (fetchContentController) { fetchContentController.abort() }
        fetchContentController = new AbortController();
        const signal = fetchContentController.signal;

        getSpotlightData(signal).then((data) => {
            const transformedData = transformToSpotlightData(data);
            setSpotlightData(transformedData);
            window.localStorage.setItem("spotlight-data", JSON.stringify(transformedData));
        })
        .catch((err) => {
            const cachedSpotlightData = window.localStorage.getItem("spotlight-data");
                
            if (!spotlightData?.items && cachedSpotlightData) {
                try {
                    const parsed: ISpotlightData = JSON.parse(cachedSpotlightData);
                    setSpotlightData(parsed);
                    setIsLoading(false);
                    return;
                } catch (error) {
                    console.error("Failed to parse cached spotlight data:", error, cachedSpotlightData);
                    window.localStorage.removeItem("spotlight-data");
                }
            }

            console.error("Error", err)
        })
        .finally(()=>{ 
            setIsLoading(false);
        })
    }

    function getSpotlightItem() {
        setIsLoadingSpotlightItem(true);

        if (spotlightData?.items) {
            const items = spotlightData.items;

            //to iterate through the items in the carousel we store the id of the last displayed item in local storage
            const storedId = window.localStorage.getItem('spotlight-carousel-id');
            let currentId: number;
            let previousId: number | null = storedId
                ? Number(storedId) 
                : null;

            //if we have no saved id from the last displayed spotlight item, we display the first item.
            //if we have a saved id from the displayed spotlight item, we display the next in the array
            if (previousId) {
                const currentIndex = items.findIndex(item => item.id === previousId);

                if (currentIndex !== -1) {
                    //we want to display the next item in the array, 
                    //or if the currentIndex is the last, then we want to display the first item in the array.
                    const nextIndex = (currentIndex + 1) % items.length;
                    currentId = items[nextIndex].id;
                } else {
                    currentId = items[0].id;
                }
            } else {
                currentId = items[0].id;
            }
            //we set the id of the spotlight item we are showing now in local storage
            window.localStorage.setItem('spotlight-carousel-id', String(currentId));
            setSpotlightItem(items.find(item => item.id === currentId));
            setIsLoadingSpotlightItem(false)
        }
    }

    const context = useMemo(() => ({
        isLoading,
        setIsLoading,
        isLoadingSpotlightItem: isLoadingSpotlightItem,
        setIsLoadingSpotlightItem: setIsLoadingSpotlightItem,
        spotlightData: spotlightData,
        setSpotlightData: setSpotlightData,
        spotlightItem: spotlightItem,
        setSpotlightItem: setSpotlightItem,
        getSpotlight
        }), [spotlightData, spotlightItem, isLoadingSpotlightItem, isLoading]
    );
    
    return (
      <SpotlightContext.Provider value={context}>
        {children}
      </SpotlightContext.Provider>
    );
};

export default SpotlightProvider;
